/**
 * Tiny synthesised sound effects (no audio files). Browsers block audio until a user
 * gesture, so `unlock()` is called from the first tap.
 */
export class Sfx {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  enabled = true;

  unlock(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    this.ctx = new Ctor();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.5;
    this.master.connect(this.ctx.destination);
  }

  private tone(freq: number, dur: number, type: OscillatorType = 'sine', gain = 0.6, slideTo?: number, at = 0): void {
    if (!this.enabled || !this.ctx || !this.master) return;
    const t0 = this.ctx.currentTime + at;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, t0);
    if (slideTo) o.frequency.exponentialRampToValueAtTime(slideTo, t0 + dur);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    o.connect(g).connect(this.master);
    o.start(t0);
    o.stop(t0 + dur + 0.02);
  }

  private noise(dur: number, gain = 0.5, lowpass = 1200): void {
    if (!this.enabled || !this.ctx || !this.master) return;
    const ctx = this.ctx;
    const buf = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * dur), ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length) ** 2;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = lowpass;
    const g = ctx.createGain();
    g.gain.value = gain;
    src.connect(f).connect(g).connect(this.master);
    src.start();
  }

  /** a correct "say" */
  tick(): void {
    this.tone(880, 0.06, 'triangle', 0.35);
  }
  /** a card lands */
  flip(): void {
    this.noise(0.05, 0.12, 3000);
  }
  /** a correct slap / gesture — a thump and a rising whoosh */
  slap(): void {
    this.noise(0.12, 0.6, 900);
    this.tone(160, 0.12, 'sine', 0.5, 60);
    this.tone(400, 0.25, 'sine', 0.25, 1400, 0.08);
  }
  /** a mistake */
  buzz(): void {
    this.tone(140, 0.28, 'sawtooth', 0.35, 90);
    this.tone(146, 0.28, 'square', 0.15, 95);
  }
  /** the round is over */
  win(): void {
    const notes = [523, 659, 784, 1047];
    notes.forEach((f, i) => this.tone(f, 0.18, 'triangle', 0.4, undefined, i * 0.12));
    this.tone(1319, 0.5, 'triangle', 0.35, undefined, notes.length * 0.12);
  }
  /** a gorilla beat */
  thump(): void {
    this.tone(110, 0.1, 'sine', 0.6, 70);
  }
}
